import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Plus,
  X,
  CheckCircle2,
  ArrowRight,
  Mail,
  ListTodo,
  Building2,
  Pencil,
  Trash2,
  User,
  Calendar,
  Check,
  Send,
  XCircle,
  FileText,
  ChevronRight,
  AlertCircle,
  Clock,
  FileClock
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import RichTextEditor from '../../components/RichTextEditor';
import IsolatedRichTextField from '../../components/IsolatedRichTextField';
import RichTextDisplay from '../../components/RichTextDisplay';
import { MissingFieldsAlert } from '@/components/MissingFieldsAlert';
import { WonPrompt } from '@/components/WonPrompt';
import { EntityStatusLogTab } from '@/components/EntityStatusLogTab';

const SOURCES = ['Google', 'Word of Mouth', 'Social Media', 'Email Blast', 'Conference', 'LinkedIn', 'Referral', 'Website'];
// Linear progression that "Advance Stage" walks through. Terminal stages
// (Won / Lost) are reached via Approve & Create Proposal / Reject.
const LEAD_ADVANCEABLE_STAGES = ['New Lead', 'Researching', 'Outreach', 'Under Review'];
const DRAFT_STATUS_COLORS = {
  pending_review: 'bg-amber-500/15 text-amber-400',
  approved: 'bg-emerald-500/15 text-emerald-400',
  needs_revision: 'bg-destructive/15 text-destructive',
};

const LEAD_JURISDICTION_OPTIONS = [
  { value: 'nyc', label: 'NYC' },
  { value: 'nassau_county', label: 'Nassau County' },
  { value: 'suffolk_county', label: 'Suffolk County' },
  { value: 'westchester', label: 'Westchester' },
  { value: 'other', label: 'Other' },
];

export function LeadDetailPanel({ open, onOpenChange, lead, isCreate, users, allTags, stages, stageDescriptions, onRefresh, onLeadsRefresh, onTagsRefresh }) {
  const { hasPerm } = useAuth();
  const canApproveProposals = !!hasPerm('approve_proposals');
  const [form, setForm] = useState({});
  // Isolated refs for the two RichTextEditors that live on this large
  // panel. Keeping their value out of `form` state prevents each keystroke
  // from re-rendering the entire 1200+ line panel (which causes visible
  // typing lag on ReactQuill). We read the current values from the refs at
  // save time.
  const notesRef = useRef(null);
  const commentsRef = useRef(null);
  const [nextSteps, setNextSteps] = useState([]);
  const [emailDrafts, setEmailDrafts] = useState([]);
  const [todos, setTodos] = useState([]);
  const [nextStepOptions, setNextStepOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState(SOURCES);
  const [nsFormOpen, setNsFormOpen] = useState(false);
  const [nsForm, setNsForm] = useState({ step_text: '', assigned_to: '', due_date: '', priority: 'Medium', todo_title: '', todo_description: '' });
  const [editingNsId, setEditingNsId] = useState(null);
  const [editNsForm, setEditNsForm] = useState({});
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [editTodoForm, setEditTodoForm] = useState({});
  const [draftFormOpen, setDraftFormOpen] = useState(false);
  const [draftForm, setDraftForm] = useState({ subject: '', body: '' });
  const [viewDraft, setViewDraft] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [timeEntries, setTimeEntries] = useState([]);
  const [statusLog, setStatusLog] = useState([]);

  const leadId = lead?.id;

  const fetchNextSteps = () => {
    if (!leadId) return;
    api.get(`/leads/${leadId}/next-steps`).then(r => setNextSteps(r.data || [])).catch(() => {});
  };

  const fetchEmailDrafts = () => {
    if (!leadId) return;
    api.get(`/leads/${leadId}/email-drafts`).then(r => setEmailDrafts(r.data || [])).catch(() => {});
  };

  const fetchTodos = () => {
    if (!leadId) return;
    api.get(`/leads/${leadId}/todos`).then(r => setTodos(r.data || [])).catch(() => {});
  };

  const loadLeadData = (id) => {
    api.get(`/leads/${id}/next-steps`).then(r => setNextSteps(r.data || [])).catch(() => {});
    api.get(`/leads/${id}/email-drafts`).then(r => setEmailDrafts(r.data || [])).catch(() => {});
    api.get(`/leads/${id}/todos`).then(r => setTodos(r.data || [])).catch(() => {});
    // Pull time logged + status log against this lead from the enriched GET endpoint.
    api.get(`/leads/${id}`)
      .then(r => {
        setTimeEntries(r.data?.time_entries || []);
        setStatusLog(r.data?.status_log || []);
      })
      .catch(() => {
        setTimeEntries([]);
        setStatusLog([]);
      });
  };

  // Reset/sync form state when the lead or mode changes — handled during render
  // via the "adjusting state when a prop changes" pattern to avoid
  // react-hooks/set-state-in-effect warnings.
  const [syncedFor, setSyncedFor] = useState({ isCreate: null, leadId: null });
  if (syncedFor.isCreate !== isCreate || syncedFor.leadId !== leadId) {
    setSyncedFor({ isCreate, leadId });
    if (isCreate) {
      setForm({ contact_name: '', company: '', email: '', phone: '', source: '', date_received: new Date().toISOString().slice(0, 10), notes: '', project_address: '', pipeline_stage: 'New Lead', tags: [], due_date: '', owner_id: '', project_manager_ids: [], save_as_client: false, linked_client_id: null, linked_contact_id: null });
      setNextSteps([]);
      setEmailDrafts([]);
      setTodos([]);
      setActiveTab('overview');
    } else if (leadId) {
      setForm({ ...lead });
    }
  }

  useEffect(() => {
    if (!isCreate && leadId) loadLeadData(leadId);
  }, [leadId, isCreate]);

  useEffect(() => {
    api.get('/leads/next-step-options').then(r => {
      setNextStepOptions([...(r.data.default || []), ...(r.data.custom || [])]);
    }).catch(() => {});
    api.get('/leads/source-options').then(r => {
      setSourceOptions([...(r.data.default || SOURCES), ...(r.data.custom || [])]);
    }).catch(() => setSourceOptions(SOURCES));
  }, []);

  // Client + Contact autocomplete/linking data - fetched once, used across Lead form
  const [allClients, setAllClients] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [closeWonOpen, setCloseWonOpen] = useState(false);
  const [closeWonClientMode, setCloseWonClientMode] = useState('new'); // 'new' | 'existing'
  const [closeWonExistingClientId, setCloseWonExistingClientId] = useState('');
  useEffect(() => {
    api.get('/clients').then(r => setAllClients((r.data || []).filter(c => c.is_active !== false))).catch(() => {});
    api.get('/contacts').then(r => setAllContacts(r.data || [])).catch(() => {});
  }, []);

  // If user selects an existing client, auto-fill company/email/phone for convenience
  const applyLinkedClient = (client) => {
    if (!client) {
      setForm(prev => ({ ...prev, linked_client_id: null, linked_contact_id: null }));
      return;
    }
    setForm(prev => ({
      ...prev,
      linked_client_id: client.id,
      linked_contact_id: null,
      company: prev.company || client.name || '',
      email: prev.email || client.email || '',
      phone: prev.phone || client.phone || '',
      // Project Address is intentionally NOT copied from the client's mailing
      // address - projects are often at a different site than the client HQ
      // and the auto-fill caused careless mistakes on the Existing Client
      // fast-track. Users enter the correct site address manually.
    }));
  };

  // Fuzzy-match clients on typed company name (case-insensitive contains)
  const companyMatches = (form.company || '').trim().length >= 2 && !form.linked_client_id
    ? allClients
        .filter(c => (c.name || '').toLowerCase().includes((form.company || '').toLowerCase()))
        .slice(0, 5)
    : [];

  // If linked_client_id is set, filter contacts to only those linked to that client
  const contactsForLinkedClient = form.linked_client_id
    ? allContacts.filter(c => (c.linked_clients || []).includes(form.linked_client_id))
    : allContacts;

  // Fuzzy-match contacts on typed contact_name (case-insensitive)
  const contactMatches = (form.contact_name || '').trim().length >= 2 && !form.linked_contact_id
    ? contactsForLinkedClient
        .filter(c => (c.name || '').toLowerCase().includes((form.contact_name || '').toLowerCase()))
        .slice(0, 5)
    : [];

  const applyLinkedContact = (contact) => {
    if (!contact) {
      setForm(prev => ({ ...prev, linked_contact_id: null }));
      return;
    }
    // Selecting a Primary Contact is an explicit action so we OVERWRITE the
    // email + phone fields with the contact's values (previously used a
    // fallback OR which quietly kept stale placeholder text). Contact name
    // still uses the OR fallback because users sometimes type it before
    // linking a contact and we don't want to clobber that.
    setForm(prev => ({
      ...prev,
      linked_contact_id: contact.id,
      contact_name: prev.contact_name || contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
    }));
  };

  const handleSave = async () => {
    if (!form.contact_name?.trim()) return toast.error('Contact name required');
    if (!form.project_address?.trim()) return toast.error('Project Address is required');
    if (!form.source?.trim()) return toast.error('Source is required');
    // Flush isolated RTE fields into the submit payload
    const payload = {
      ...form,
      notes: notesRef.current?.getValue() ?? form.notes ?? '',
      comments: commentsRef.current?.getValue() ?? form.comments ?? '',
    };
    const wasWonBefore = lead?.pipeline_stage === 'Won';
    const isWonNow = form.pipeline_stage === 'Won';
    try {
      if (isCreate) {
        const r = await api.post('/leads', payload);
        toast.success('Lead created');
        onRefresh(r.data);
      } else {
        const r = await api.put(`/leads/${lead.id}`, payload);
        toast.success('Lead updated');
        onRefresh(r.data);
        // If user just flipped this lead to Won AND the backend didn't
        // already create/find a proposal, nudge them. Note: we check the
        // *response* not the stale pre-PUT lead, because the backend
        // auto-creates a proposal on Won transitions.
        if (isWonNow && !wasWonBefore && !r.data?.proposal_id) {
          setWonPromptOpen(true);
        }
      }
      onLeadsRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving lead'); }
  };

  // Won prompt — appears when stage is flipped to Won and no proposal exists yet.
  const [wonPromptOpen, setWonPromptOpen] = useState(false);

  // Recommended-fields check (non-blocking) — surfaces a pill at the top of
  // the panel listing whichever key fields are still empty.
  const missingFields = (() => {
    if (isCreate || !lead) return [];
    const isWon = (form.pipeline_stage || lead.pipeline_stage) === 'Won';
    return [
      { label: 'Email', present: !!(form.email || lead.email)?.toString().trim() },
      { label: 'Phone', present: !!(form.phone || lead.phone)?.toString().trim() },
      { label: 'Project Address', present: !!(form.project_address || lead.project_address)?.toString().trim() },
      { label: 'Source', present: !!(form.source || lead.source)?.toString().trim() },
      // Estimated value only matters once the lead is Won.
      ...(isWon ? [{ label: 'Estimated Value', present: !!(form.estimated_value || lead.estimated_value) }] : []),
    ];
  })();

  const navigate = useNavigate();

  // ─── Advance / Close gating ──────────────────────────────────────────────
  // Project Address and Source are required before any stage advance OR before
  // closing a lead (Approve → Won, Reject → Lost). Mirrors the Proposals flow.
  const advanceBlockReason = (() => {
    if (!(form.project_address || lead?.project_address)?.toString().trim()) return 'Project Address is required';
    if (!(form.source || lead?.source)?.toString().trim()) return 'Source is required';
    return null;
  })();

  // New-proposal requirement (enforced at lead -> proposal conversion only):
  // at least one jurisdiction tag and at least one project manager. Used to
  // block the Approve / Create-Proposal actions until both are set. Legacy
  // string jurisdictions (if any) are treated as unset so the user picks tags.
  const proposalFieldsBlockReason = (() => {
    const jurList = Array.isArray(form.jurisdiction) ? form.jurisdiction.filter(Boolean) : [];
    const jurOk = jurList.length > 0 && !(jurList.includes('other') && !(form.jurisdiction_other || '').trim());
    if (!jurOk) return 'Select a jurisdiction to create a proposal';
    const pmOk = (form.project_manager_ids || []).filter(Boolean).length > 0 || !!form.project_manager_id;
    if (!pmOk) return 'Assign a project manager to create a proposal';
    return null;
  })();
  const convertDisabled = !!advanceBlockReason || !!proposalFieldsBlockReason;

  const currentStage = form.pipeline_stage || lead?.pipeline_stage || 'New Lead';
  const advanceIdx = LEAD_ADVANCEABLE_STAGES.indexOf(currentStage);
  const canAdvance = !isCreate && lead && advanceIdx !== -1 && advanceIdx < LEAD_ADVANCEABLE_STAGES.length - 1;
  const nextStage = canAdvance ? LEAD_ADVANCEABLE_STAGES[advanceIdx + 1] : null;
  const isLeadClosed = currentStage === 'Won' || currentStage === 'Lost';

  const advanceLeadStage = async () => {
    if (!canAdvance || !nextStage) return;
    if (advanceBlockReason) return toast.error(advanceBlockReason);
    try {
      // Persist any unsaved Project Address / Source alongside the stage change
      // so the lead record always reflects the validated state.
      const r = await api.put(`/leads/${lead.id}`, {
        pipeline_stage: nextStage,
        project_address: form.project_address || '',
        source: form.source || '',
      });
      toast.success(`Advanced to ${nextStage}`);
      setForm(prev => ({ ...prev, pipeline_stage: nextStage }));
      onRefresh(r.data);
      onLeadsRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot advance stage'); }
  };

  const handleApprove = async () => {
    if (advanceBlockReason) return toast.error(advanceBlockReason);
    if (proposalFieldsBlockReason) return toast.error(proposalFieldsBlockReason);
    try {
      // Persist unsaved Project Address / Source + the new-proposal fields
      // (jurisdiction + PM) before closing the lead so the proposal inherits them.
      await api.put(`/leads/${lead.id}`, {
        project_address: form.project_address || '',
        source: form.source || '',
        jurisdiction: Array.isArray(form.jurisdiction) ? form.jurisdiction : [],
        jurisdiction_other: form.jurisdiction_other || '',
        project_manager_ids: (form.project_manager_ids || []).filter(Boolean),
      });
      const r = await api.post(`/leads/${lead.id}/approve`);
      toast.success(`Lead approved. Proposal ${r.data.proposal?.proposal_number} created`);
      onLeadsRefresh();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error approving lead'); }
  };

  const handleCreateProposal = async () => {
    if (advanceBlockReason) return toast.error(advanceBlockReason);
    if (proposalFieldsBlockReason) return toast.error(proposalFieldsBlockReason);
    // If lead has a linked client, just create. Otherwise prompt user to pick.
    if (form.linked_client_id || lead.linked_client_id || lead.client_id) {
      return doCreateProposal();
    }
    // No client linked yet - open the picker dialog
    setCloseWonClientMode('new');
    setCloseWonExistingClientId('');
    setCloseWonOpen(true);
  };

  const doCreateProposal = async (clientChoice = null) => {
    try {
      const payload = {
        pipeline_stage: 'Won',
        project_address: form.project_address || '',
        source: form.source || '',
        jurisdiction: Array.isArray(form.jurisdiction) ? form.jurisdiction : [],
        jurisdiction_other: form.jurisdiction_other || '',
        project_manager_ids: (form.project_manager_ids || []).filter(Boolean),
      };
      if (clientChoice) payload.__close_won_client = clientChoice;
      const r = await api.put(`/leads/${lead.id}`, payload);
      const proposalId = r.data?.proposal_id;
      if (proposalId) {
        toast.success('Proposal created');
        navigate('/proposals', { state: { openProposalId: proposalId } });
      } else {
        const r2 = await api.post(`/leads/${lead.id}/approve`);
        toast.success(`Proposal ${r2.data.proposal?.proposal_number} created`);
        onLeadsRefresh();
        onOpenChange(false);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating proposal'); }
  };

  const handleReject = async () => {
    if (advanceBlockReason) return toast.error(advanceBlockReason);
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try {
      // Persist any unsaved Project Address / Source before closing the lead
      await api.put(`/leads/${lead.id}`, {
        project_address: form.project_address || '',
        source: form.source || '',
      });
      await api.post(`/leads/${lead.id}/reject`, { reason });
      toast.success('Lead rejected');
      onLeadsRefresh();
      onOpenChange(false);
    } catch (e) { toast.error('Error rejecting lead'); }
  };

  // Tags
  const addTag = async (tagName) => {
    const trimmed = tagName.trim();
    if (!trimmed) return;
    const current = form.tags || [];
    if (current.includes(trimmed)) return;
    const updatedTags = [...current, trimmed];
    setForm(prev => ({ ...prev, tags: updatedTags }));
    setTagInput('');
    if (!isCreate && lead?.id) {
      await api.put(`/leads/${lead.id}`, { tags: updatedTags });
      // Also save the tag globally if new
      if (!allTags.includes(trimmed)) {
        api.post('/leads/tags', { name: trimmed }).catch(() => {});
        onTagsRefresh();
      }
    }
  };

  const removeTag = async (tag) => {
    const updatedTags = (form.tags || []).filter(t => t !== tag);
    setForm(prev => ({ ...prev, tags: updatedTags }));
    if (!isCreate && lead?.id) {
      await api.put(`/leads/${lead.id}`, { tags: updatedTags });
    }
  };

  // Next Steps
  const createNextStep = async () => {
    if (!nsForm.step_text.trim()) return toast.error('Step text required');
    try {
      await api.post(`/leads/${lead.id}/next-steps`, {
        ...nsForm,
        todo_title: nsForm.todo_title || nsForm.step_text,
      });
      toast.success('Next step added with linked To-Do');
      setNsFormOpen(false);
      setNsForm({ step_text: '', assigned_to: '', due_date: '', priority: 'Medium', todo_title: '', todo_description: '' });
      fetchNextSteps();
      fetchTodos();
      onLeadsRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating next step'); }
  };

  const toggleNextStep = async (nsId, currentVal) => {
    try {
      await api.put(`/leads/${lead.id}/next-steps/${nsId}`, { is_completed: !currentVal });
      fetchNextSteps();
      fetchTodos();
      onLeadsRefresh();
    } catch (e) { toast.error('Error updating step'); }
  };

  const deleteNextStep = async (nsId) => {
    if (!window.confirm('Delete this next step and its linked To-Do?')) return;
    try {
      await api.delete(`/leads/${lead.id}/next-steps/${nsId}`);
      toast.success('Step deleted');
      fetchNextSteps();
      fetchTodos();
      onLeadsRefresh();
    } catch (e) { toast.error('Error deleting step'); }
  };

  const openEditNs = (ns) => {
    setEditingNsId(ns.id);
    setEditNsForm({
      step_text: ns.step_text || '',
      assigned_to: ns.assigned_to || '',
      due_date: ns.due_date || '',
    });
  };

  const saveEditNs = async () => {
    if (!editNsForm.step_text.trim()) return toast.error('Step text required');
    try {
      await api.put(`/leads/${lead.id}/next-steps/${editingNsId}`, {
        step_text: editNsForm.step_text.trim(),
        assigned_to: editNsForm.assigned_to || null,
        due_date: editNsForm.due_date || null,
      });
      toast.success('Step updated');
      setEditingNsId(null);
      fetchNextSteps();
      onLeadsRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error updating step'); }
  };

  const openEditTodo = (t) => {
    setEditingTodoId(t.id);
    setEditTodoForm({
      title: t.title || '',
      description: t.description || '',
      assigned_to: t.assigned_to || '',
      due_date: t.due_date || '',
      priority: t.priority || 'Medium',
      status: t.status || 'Not Started',
    });
  };

  const saveEditTodo = async () => {
    if (!editTodoForm.title.trim()) return toast.error('Title required');
    try {
      await api.put(`/tasks/${editingTodoId}`, {
        title: editTodoForm.title.trim(),
        description: editTodoForm.description,
        assigned_to: editTodoForm.assigned_to || null,
        due_date: editTodoForm.due_date || null,
        priority: editTodoForm.priority,
        status: editTodoForm.status,
      });
      toast.success('To-Do updated');
      setEditingTodoId(null);
      fetchTodos();
      fetchNextSteps();
      onLeadsRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error updating to-do'); }
  };

  // Email Drafts
  const submitDraft = async () => {
    if (!draftForm.subject.trim()) return toast.error('Subject required');
    if (!draftForm.body.trim()) return toast.error('Draft body required');
    try {
      await api.post(`/leads/${lead.id}/email-drafts`, draftForm);
      toast.success('Draft submitted for review. Owners notified.');
      setDraftFormOpen(false);
      setDraftForm({ subject: '', body: '' });
      fetchEmailDrafts();
      onLeadsRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating draft'); }
  };

  const approveDraft = async (draftId) => {
    try {
      await api.put(`/leads/${lead.id}/email-drafts/${draftId}/approve`, { note: '' });
      toast.success('Draft approved. Office managers notified.');
      fetchEmailDrafts();
      onLeadsRefresh();
      setViewDraft(null);
    } catch (e) { toast.error('Error approving draft'); }
  };

  const rejectDraft = async (draftId) => {
    const note = window.prompt('Revision note:');
    if (note === null) return;
    try {
      await api.put(`/leads/${lead.id}/email-drafts/${draftId}/reject`, { note });
      toast.success('Revision requested');
      fetchEmailDrafts();
      setViewDraft(null);
    } catch (e) { toast.error('Error'); }
  };

  // To-Do status update
  const toggleTodoStatus = async (todoId, currentStatus) => {
    const newStatus = currentStatus === 'Completed' ? 'In Progress' : 'Completed';
    try {
      await api.put(`/tasks/${todoId}`, { status: newStatus });
      fetchTodos();
      fetchNextSteps();
      onLeadsRefresh();
    } catch (e) { toast.error('Error updating to-do'); }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto" data-testid="lead-detail-sheet">
        <SheetHeader className="space-y-1.5">
          {!isCreate && lead?.id && (
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-mono text-muted-foreground w-fit">LEAD-{String(lead.id).slice(0, 8)}</p>
              <MissingFieldsAlert fields={missingFields} testId="lead-missing-fields" />
            </div>
          )}
          <SheetTitle className="text-[18px] font-medium leading-tight">
            {isCreate ? 'New Lead' : (form.company || form.contact_name || 'Lead Details')}
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            {isCreate ? 'Fill in the lead information below' : `${form.contact_name || ''}${form.email ? ` · ${form.email}` : ''}`}
          </SheetDescription>
        </SheetHeader>

        {/* Lead won → prompt to create a proposal next */}
        <WonPrompt
          open={wonPromptOpen}
          onOpenChange={setWonPromptOpen}
          title="Lead won. Create a proposal?"
          description="You just marked this lead as Won but haven't created a proposal yet. Want to create one now so this lead flows into the next stage of the pipeline?"
          primaryLabel="Create proposal"
          onConfirm={handleCreateProposal}
          testId="lead-won-prompt"
        />

        {/* Pipeline Bar — full horizontal stage bar mirroring Proposals' layout */}
        {!isCreate && lead && stages.length > 0 && (
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-1 mt-3" data-testid="lead-pipeline">
              {stages.map((stage, i) => {
                const ci = stages.indexOf(currentStage);
                const si = i;
                const stageCls = si < ci
                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                  : si === ci
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-secondary text-muted-foreground border-border';
                return (
                  <div key={stage} className="flex items-center flex-1 min-w-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={`flex-1 text-center py-1.5 px-2 rounded text-xs font-semibold border cursor-help truncate ${stageCls}`} data-testid={`lead-pipeline-chip-${stage.replace(/\s+/g, '-').toLowerCase()}`}>
                          {stage}
                        </div>
                      </TooltipTrigger>
                      {stageDescriptions?.[stage] && (
                        <TooltipContent side="bottom" className="max-w-[220px] text-xs"><p>{stageDescriptions[stage]}</p></TooltipContent>
                      )}
                    </Tooltip>
                    {i < stages.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground mx-0.5 shrink-0" />}
                  </div>
                );
              })}
            </div>
          </TooltipProvider>
        )}

        {/* Current stage description (mirrors Proposals) */}
        {!isCreate && lead && stageDescriptions?.[currentStage] && (
          <p className="text-xs text-muted-foreground px-1 mt-2" data-testid="lead-stage-description">
            {stageDescriptions[currentStage]}
          </p>
        )}

        {/* Action buttons row — Advance Stage + stage-specific actions */}
        {!isCreate && lead && !isLeadClosed && (
          <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="lead-actions-row">
            {canAdvance && currentStage !== 'Under Review' && (
              <Button size="sm" onClick={advanceLeadStage} className="bg-primary text-primary-foreground" disabled={!!advanceBlockReason} data-testid="lead-advance-stage-btn">
                Advance Stage
              </Button>
            )}
            {currentStage === 'Under Review' && lead.status === 'new' && canApproveProposals && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={handleApprove} disabled={convertDisabled} data-testid="approve-lead-btn">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Approve & Create Proposal
                </Button>
                <Button size="sm" variant="outline" className="text-xs text-destructive border-destructive/20 hover:bg-destructive/10" onClick={handleReject} disabled={!!advanceBlockReason} data-testid="reject-lead-btn">
                  <XCircle className="h-3 w-3 mr-1" /> Reject
                </Button>
              </>
            )}
            {/* Existing Client fast-track: skip the Researching/Outreach/Under-Review
                progression because the client is already known - jump straight to
                Won + Proposal. Required-field validation still applies. */}
            {(form.source || lead?.source) === 'Existing Client'
              && currentStage !== 'Under Review'
              && lead.status === 'new'
              && canApproveProposals && (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                onClick={handleApprove}
                disabled={convertDisabled}
                data-testid="lead-existing-client-fast-track-btn"
                title="Skip pre-sales stages and create a proposal directly for this existing client"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" /> Close as Won & Create Proposal
              </Button>
            )}
            {currentStage === 'Under Review' && lead.status === 'new' && !canApproveProposals && (
              <span className="text-xs text-muted-foreground" data-testid="lead-approve-perm-denied">You don&apos;t have permission to approve/reject leads.</span>
            )}
            {advanceBlockReason && (
              <span className="text-xs text-amber-400 flex items-center gap-1" data-testid="lead-advance-block-reason">
                <AlertCircle className="h-3 w-3" /> {advanceBlockReason}
              </span>
            )}
            {!advanceBlockReason && proposalFieldsBlockReason && canApproveProposals && lead.status === 'new'
              && (currentStage === 'Under Review' || ((form.source || lead?.source) === 'Existing Client' && currentStage !== 'Under Review')) && (
              <span className="text-xs text-amber-400 flex items-center gap-1" data-testid="lead-proposal-fields-block-reason">
                <AlertCircle className="h-3 w-3" /> {proposalFieldsBlockReason}
              </span>
            )}
          </div>
        )}

        {/* Fallback: Won leads with no linked proposal */}
        {!isCreate && lead && (form.pipeline_stage || lead.pipeline_stage) === 'Won' && !lead.proposal_id && (
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={handleCreateProposal} disabled={convertDisabled} data-testid="create-proposal-btn">
              <FileText className="h-3 w-3 mr-1" /> Create Proposal
            </Button>
            {convertDisabled ? (
              <span className="text-xs text-amber-400 flex items-center gap-1" data-testid="create-proposal-block-reason">
                <AlertCircle className="h-3 w-3" /> {advanceBlockReason || proposalFieldsBlockReason}
              </span>
            ) : (
              <p className="text-xs text-muted-foreground">No linked proposal found for this Won lead.</p>
            )}
          </div>
        )}

        {/* Link to existing proposal for Won leads */}
        {!isCreate && lead && (form.pipeline_stage || lead.pipeline_stage) === 'Won' && lead.proposal_id && (
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" variant="outline" className="text-xs text-emerald-400 border-emerald-500/20" onClick={() => navigate('/proposals', { state: { openProposalId: lead.proposal_id } })} data-testid="view-proposal-btn">
              <FileText className="h-3 w-3 mr-1" /> View Proposal {lead.proposal_number || ''}
            </Button>
          </div>
        )}

        <div className="mt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
              {!isCreate && (
                <>
                  <TabsTrigger value="next-steps" className="text-xs">
                    <ArrowRight className="h-3 w-3 mr-1" /> Steps {nextSteps.length > 0 && <Badge variant="outline" className="ml-1 text-xs h-4 px-1">{nextSteps.filter(s => !s.is_completed).length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="email-drafts" className="text-xs">
                    <Mail className="h-3 w-3 mr-1" /> Drafts {emailDrafts.filter(d => d.draft_status === 'pending_review').length > 0 && <Badge className="ml-1 text-xs h-4 px-1 bg-amber-500/20 text-amber-400">{emailDrafts.filter(d => d.draft_status === 'pending_review').length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="todos" className="text-xs">
                    <ListTodo className="h-3 w-3 mr-1" /> To-Dos {todos.length > 0 && <Badge variant="outline" className="ml-1 text-xs h-4 px-1">{todos.filter(t => t.status !== 'Completed').length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="time-logged" className="text-xs" data-testid="lead-tab-time-logged">
                    <Clock className="h-3 w-3 mr-1" /> Time {timeEntries.length > 0 && <Badge variant="outline" className="ml-1 text-xs h-4 px-1">{timeEntries.reduce((s, t) => s + (Number(t.hours) || 0), 0).toFixed(1)}h</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="status-log" className="text-xs" data-testid="lead-tab-status-log">
                    <FileClock className="h-3 w-3 mr-1" /> Status Log {statusLog.length > 0 && <Badge variant="outline" className="ml-1 text-xs h-4 px-1">{statusLog.length}</Badge>}
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-3 space-y-3">
              {/* Link to existing client toggle + link state */}
              <div className="rounded-md border border-border bg-secondary/20 p-2.5 space-y-2" data-testid="lead-link-existing-section">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={!!form.linked_client_id}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          applyLinkedClient(null);
                        }
                      }}
                      aria-label="Link to existing client" data-testid="lead-link-existing-toggle"
                    />
                    <Label className="text-xs font-medium cursor-pointer">Link to existing client</Label>
                  </div>
                  {form.linked_client_id && (
                    <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                      <Check className="h-2.5 w-2.5 mr-1" />
                      Linked: {allClients.find(c => c.id === form.linked_client_id)?.name || '(unknown)'}
                    </Badge>
                  )}
                </div>
                {!form.linked_client_id && (
                  <SearchableSelect
                    value=""
                    onValueChange={(id) => applyLinkedClient(allClients.find(c => c.id === id))}
                    options={[{ value: '', label: 'Search and select a client...' }, ...allClients.map(c => ({ value: c.id, label: c.name }))]}
                    placeholder="Search clients..."
                    className="bg-background border-border text-xs h-8"
                    aria-label="Search and select a client" data-testid="lead-link-client-select"
                  />
                )}
                {form.linked_client_id && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Primary Contact (optional)</Label>
                    <SearchableSelect
                      value={form.linked_contact_id || ''}
                      onValueChange={(id) => applyLinkedContact(allContacts.find(c => c.id === id))}
                      options={[{ value: '', label: 'No contact selected' }, ...contactsForLinkedClient.map(c => ({ value: c.id, label: `${c.name}${c.job_title ? ` · ${c.job_title}` : ''}` }))]}
                      placeholder="Link existing contact..."
                      className="bg-background border-border text-xs h-8"
                      aria-label="Primary contact" data-testid="lead-link-contact-select"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <Label className="text-xs text-muted-foreground">Contact Name *</Label>
                  <Input
                    value={form.contact_name || ''}
                    onChange={e => setForm({ ...form, contact_name: e.target.value, linked_contact_id: null })}
                    className="bg-background border-border"
                    aria-label="Contact name" data-testid="lead-form-name"
                  />
                  {contactMatches.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 rounded-md border border-border bg-card shadow-lg max-h-40 overflow-auto" data-testid="lead-contact-suggestions">
                      {contactMatches.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => applyLinkedContact(c)}
                          className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-secondary/50 border-b border-border last:border-0 flex items-center justify-between"
                          data-testid={`lead-contact-suggestion-${c.id}`}
                        >
                          <span>{c.name}{c.job_title && <span className="text-muted-foreground"> · {c.job_title}</span>}</span>
                          <span className="text-xs text-emerald-400">Use existing</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Label className="text-xs text-muted-foreground">Company</Label>
                  <Input
                    value={form.company || ''}
                    onChange={e => setForm({ ...form, company: e.target.value, linked_client_id: null, linked_contact_id: null })}
                    className="bg-background border-border"
                    disabled={!!form.linked_client_id}
                    aria-label="Company" data-testid="lead-form-company"
                  />
                  {companyMatches.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 rounded-md border border-border bg-card shadow-lg max-h-40 overflow-auto" data-testid="lead-company-suggestions">
                      {companyMatches.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => applyLinkedClient(c)}
                          className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-secondary/50 border-b border-border last:border-0 flex items-center justify-between"
                          data-testid={`lead-company-suggestion-${c.id}`}
                        >
                          <span>{c.name}</span>
                          <span className="text-xs text-emerald-400">Link existing</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Email</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-border" aria-label="Email" /></div>
                <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-background border-border" aria-label="Phone" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Source <span className="text-destructive">*</span></Label>
                  <SearchableSelect
                    value={form.source || ''}
                    onValueChange={v => setForm({ ...form, source: v })}
                    options={sourceOptions.map(s => ({ value: s, label: s }))}
                    placeholder="Select source..."
                    searchPlaceholder="Search or add new..."
                    emptyMessage="No matches"
                    allowCustom
                    onAddCustom={async (name) => {
                      try {
                        await api.post('/leads/source-options', { name });
                        setSourceOptions(prev => [...prev, name]);
                        toast.success(`Added source: ${name}`);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Could not add source');
                      }
                    }}
                    aria-label="Source" data-testid="lead-source-select"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Owner</Label>
                  <Select value={form.owner_id || '_none'} onValueChange={v => setForm({ ...form, owner_id: v === '_none' ? '' : v })}>
                    <SelectTrigger className="bg-background border-border" aria-label="Owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="_none" className="text-xs">Unassigned</SelectItem>
                      {users.filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Due Date</Label><Input type="date" value={form.due_date || ''} onChange={e => setForm({ ...form, due_date: e.target.value })} className="bg-background border-border" aria-label="Due date" data-testid="lead-due-date" /></div>
                <div><Label className="text-xs text-muted-foreground">Date Received</Label><Input type="date" value={form.date_received || ''} onChange={e => setForm({ ...form, date_received: e.target.value })} className="bg-background border-border" aria-label="Date received" /></div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Project Address <span className="text-destructive">*</span></Label>
                <Input value={form.project_address || ''} onChange={e => setForm({ ...form, project_address: e.target.value })} className="bg-background border-border" placeholder="Project address (required)" aria-label="Project address" data-testid="lead-address" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Project Manager <span className="text-destructive">*</span> <span className="font-normal text-muted-foreground/70">(required to create a proposal)</span></Label>
                <Select value={(form.project_manager_ids || [])[0] || form.project_manager_id || '_none'} onValueChange={v => setForm({ ...form, project_manager_ids: v === '_none' ? [] : [v] })}>
                  <SelectTrigger className="bg-background border-border" aria-label="Project manager" data-testid="lead-pm-select"><SelectValue placeholder="Select PM" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="_none" className="text-xs">None</SelectItem>
                    {users.filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Jurisdiction <span className="text-destructive">*</span> <span className="font-normal text-muted-foreground/70">(required to create a proposal)</span></Label>
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="lead-jurisdiction-options">
                  {LEAD_JURISDICTION_OPTIONS.map(opt => {
                    const sel = (Array.isArray(form.jurisdiction) ? form.jurisdiction : []).includes(opt.value);
                    return (
                      <button key={opt.value} type="button"
                        onClick={() => setForm(prev => {
                          const cur = Array.isArray(prev.jurisdiction) ? prev.jurisdiction : [];
                          const next = cur.includes(opt.value) ? cur.filter(v => v !== opt.value) : [...cur, opt.value];
                          return { ...prev, jurisdiction: next };
                        })}
                        className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:border-primary/50'}`}
                        data-testid={`lead-jurisdiction-${opt.value}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {(Array.isArray(form.jurisdiction) ? form.jurisdiction : []).includes('other') && (
                  <Input value={form.jurisdiction_other || ''} onChange={e => setForm({ ...form, jurisdiction_other: e.target.value })} placeholder="Specify the other jurisdiction" className="bg-background border-border mt-1.5 h-8 text-xs" data-testid="lead-jurisdiction-other" />
                )}
              </div>

              {/* Tags */}
              <div>
                <Label className="text-xs text-muted-foreground">Tags</Label>
                <div className="flex items-center gap-1.5 flex-wrap mt-1">
                  {(form.tags || []).map(t => (
                    <Badge key={t} className="text-xs bg-primary/10 text-primary border-primary/20 gap-0.5 pr-0.5">
                      {t}
                      <button className="ml-0.5 hover:text-destructive p-0.5" onClick={() => removeTag(t)}><X className="h-2.5 w-2.5" /></button>
                    </Badge>
                  ))}
                  <div className="flex items-center gap-1">
                    <Input
                      placeholder="Add tag..."
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); } }}
                      className="h-6 w-24 text-xs bg-background border-border px-1.5"
                      list="tag-suggestions"
                    />
                    <datalist id="tag-suggestions">
                      {allTags.filter(t => !(form.tags || []).includes(t)).map(t => <option key={t} value={t} />)}
                    </datalist>
                    {tagInput.trim() && <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => addTag(tagInput)}><Plus className="h-3 w-3" /></Button>}
                  </div>
                </div>
              </div>

              <div><Label className="text-xs text-muted-foreground">Notes</Label><IsolatedRichTextField ref={notesRef} initialValue={form.notes || ''} resetKey={lead?.id || 'new'} placeholder="Lead notes..." /></div>

              <div>
                <Label className="text-xs text-muted-foreground">Comments</Label>
                <IsolatedRichTextField
                  ref={commentsRef}
                  initialValue={form.comments || ''}
                  resetKey={lead?.id || 'new'}
                  placeholder="Persistent comments visible across every stage of this lead..."
                  data-testid="lead-comments"
                />
              </div>

              {/* "Save as Client" — visible whenever the lead isn't yet linked
                  to a client (create or edit). On save the backend creates a
                  client record from the lead's free-text company info. */}
              {!form.linked_client_id && !lead?.client_id && (
                <div className="flex items-center justify-between p-3 rounded-md border border-border bg-background" data-testid="save-as-client-toggle">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-sm font-medium">Save as Client</p>
                      <p className="text-xs text-muted-foreground">Create a client record using this lead&apos;s free-text info</p>
                    </div>
                  </div>
                  <Switch checked={form.save_as_client || false} onCheckedChange={v => setForm({ ...form, save_as_client: v })} data-testid="save-as-client-switch" />
                </div>
              )}
              {!isCreate && (form.linked_client_id || lead?.client_id) && (
                <div className="flex items-center gap-2 p-3 rounded-md border border-emerald-500/20 bg-emerald-500/5" data-testid="client-already-linked">
                  <Check className="h-4 w-4 text-emerald-400" />
                  <p className="text-xs text-muted-foreground">This lead is already linked to a client. Unlink it above to create a new one instead.</p>
                </div>
              )}

              <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground mt-2" data-testid="lead-form-submit">{isCreate ? 'Create Lead' : 'Save Changes'}</Button>
            </TabsContent>

            {/* Next Steps Tab */}
            {!isCreate && (
              <TabsContent value="next-steps" className="mt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{nextSteps.filter(s => !s.is_completed).length} active step{nextSteps.filter(s => !s.is_completed).length !== 1 ? 's' : ''}</p>
                  <Button size="sm" onClick={() => setNsFormOpen(!nsFormOpen)} className="bg-primary text-primary-foreground text-xs" data-testid="add-next-step-btn"><Plus className="h-3 w-3 mr-1" /> Add Step</Button>
                </div>

                {nsFormOpen && (
                  <Card className="border-border border-dashed bg-transparent shadow-none">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground">New Next Step</p>
                      <SearchableSelect
                        value={nsForm.step_text}
                        onValueChange={v => setNsForm({ ...nsForm, step_text: v, todo_title: v })}
                        options={nextStepOptions.map(o => ({ value: o, label: o }))}
                        placeholder="Select or type a step..."
                        allowCustom
                        onAddCustom={v => setNsForm({ ...nsForm, step_text: v, todo_title: v })}
                        data-testid="next-step-select"
                      />
                      <p className="text-xs text-muted-foreground mt-1 font-semibold uppercase tracking-wider">Linked To-Do</p>
                      <Input placeholder="To-Do title" value={nsForm.todo_title || ''} onChange={e => setNsForm({ ...nsForm, todo_title: e.target.value })} className="bg-background border-border text-sm" data-testid="next-step-todo-title" />
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Assign To</Label>
                          <Select value={nsForm.assigned_to || '_none'} onValueChange={v => setNsForm({ ...nsForm, assigned_to: v === '_none' ? '' : v })}>
                            <SelectTrigger className="bg-background border-border" aria-label="Assign to"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="_none" className="text-xs">Unassigned</SelectItem>
                              {users.filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs text-muted-foreground">Due Date</Label><Input type="date" value={nsForm.due_date || ''} onChange={e => setNsForm({ ...nsForm, due_date: e.target.value })} className="bg-background border-border" /></div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={createNextStep} className="bg-primary text-primary-foreground text-xs" data-testid="save-next-step-btn"><Plus className="h-3 w-3 mr-1" /> Create Step & To-Do</Button>
                        <Button size="sm" variant="outline" onClick={() => setNsFormOpen(false)} className="text-xs">Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="space-y-1.5">
                  {nextSteps.map(ns => (
                    <div key={ns.id} className={`flex items-start gap-2.5 p-2.5 rounded-md border ${ns.is_completed ? 'border-border/50 bg-secondary/20 opacity-60' : 'border-border bg-background'}`} data-testid={`next-step-${ns.id}`}>
                      <Checkbox
                        checked={ns.is_completed}
                        onCheckedChange={() => toggleNextStep(ns.id, ns.is_completed)}
                        className="mt-0.5"
                        data-testid={`next-step-check-${ns.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        {editingNsId === ns.id ? (
                          <div className="space-y-2">
                            <Input
                              value={editNsForm.step_text}
                              onChange={e => setEditNsForm({ ...editNsForm, step_text: e.target.value })}
                              className="bg-card border-border text-sm"
                              placeholder="Step text"
                              data-testid={`edit-next-step-text-${ns.id}`}
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <Select value={editNsForm.assigned_to || 'none'} onValueChange={v => setEditNsForm({ ...editNsForm, assigned_to: v === 'none' ? '' : v })}>
                                <SelectTrigger className="bg-card border-border text-xs h-8" data-testid={`edit-next-step-assignee-${ns.id}`}>
                                  <SelectValue placeholder="Assignee" />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  <SelectItem value="none" className="text-xs text-muted-foreground">Unassigned</SelectItem>
                                  {(users || []).filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Input
                                type="date"
                                value={editNsForm.due_date}
                                onChange={e => setEditNsForm({ ...editNsForm, due_date: e.target.value })}
                                className="bg-card border-border text-xs h-8"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={saveEditNs} className="bg-primary text-primary-foreground text-xs h-7" data-testid={`save-edit-next-step-${ns.id}`}>Save</Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingNsId(null)} className="text-xs h-7">Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className={`text-sm ${ns.is_completed ? 'line-through text-muted-foreground' : ''}`}>{ns.step_text}</p>
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                              {ns.assigned_to_name && <span className="flex items-center gap-0.5"><User className="h-2.5 w-2.5" />{ns.assigned_to_name}</span>}
                              {ns.due_date && <span className="flex items-center gap-0.5"><Calendar className="h-2.5 w-2.5" />{ns.due_date}</span>}
                              {ns.is_completed && ns.completed_at && <span>Completed {new Date(ns.completed_at).toLocaleDateString()}</span>}
                            </div>
                          </>
                        )}
                      </div>
                      {editingNsId !== ns.id && (
                        <div className="flex items-center gap-0.5 shrink-0">
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => openEditNs(ns)} data-testid={`edit-next-step-${ns.id}`}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => deleteNextStep(ns.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  {nextSteps.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No next steps yet. Add one above.</p>}
                </div>
              </TabsContent>
            )}

            {/* Email Drafts Tab */}
            {!isCreate && (
              <TabsContent value="email-drafts" className="mt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{emailDrafts.length} draft{emailDrafts.length !== 1 ? 's' : ''}</p>
                  <Button size="sm" onClick={() => { setDraftFormOpen(!draftFormOpen); setViewDraft(null); }} className="bg-primary text-primary-foreground text-xs" data-testid="add-email-draft-btn"><Plus className="h-3 w-3 mr-1" /> New Draft</Button>
                </div>

                {draftFormOpen && (
                  <Card className="border-border border-dashed bg-transparent shadow-none">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground">New Email Draft</p>
                      <Input placeholder="Subject *" value={draftForm.subject} onChange={e => setDraftForm({ ...draftForm, subject: e.target.value })} className="bg-background border-border" data-testid="draft-subject" />
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Body *</Label>
                        <RichTextEditor value={draftForm.body} onChange={v => setDraftForm({ ...draftForm, body: v })} placeholder="Write your email draft..." data-testid="draft-body-editor" />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={submitDraft} className="bg-primary text-primary-foreground text-xs" data-testid="submit-draft-btn"><Send className="h-3 w-3 mr-1" /> Submit for Review</Button>
                        <Button size="sm" variant="outline" onClick={() => setDraftFormOpen(false)} className="text-xs">Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {viewDraft && (
                  <Card className="border-primary/30 bg-primary/5 shadow-none">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold">{viewDraft.subject}</p>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setViewDraft(null)}><X className="h-3 w-3" /></Button>
                      </div>
                      <div className="border border-border rounded-md p-3 bg-background">
                        <RichTextDisplay value={viewDraft.body} collapsedHeight={100} data-testid="draft-body-display" />
                      </div>
                      <div className="text-xs text-muted-foreground">By {viewDraft.created_by_name} on {new Date(viewDraft.created_at).toLocaleDateString()}</div>
                      {viewDraft.review_note && <p className="text-xs text-amber-400 italic">Note: {viewDraft.review_note}</p>}
                      {viewDraft.draft_status === 'pending_review' && (
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => approveDraft(viewDraft.id)} data-testid="approve-draft-btn">
                            <Check className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs text-amber-400 border-amber-500/20" onClick={() => rejectDraft(viewDraft.id)} data-testid="request-revision-btn">
                            <Pencil className="h-3 w-3 mr-1" /> Request Revision
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                <div className="space-y-1.5">
                  {emailDrafts.map(d => (
                    <div key={d.id} className="flex items-center justify-between p-2.5 rounded-md border border-border bg-background hover:border-primary/30 transition-colors cursor-pointer" onClick={() => { setViewDraft(d); setDraftFormOpen(false); }} data-testid={`email-draft-${d.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{d.subject}</p>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                          <span>{d.created_by_name}</span>
                          <span>{new Date(d.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Badge className={`text-xs shrink-0 ${DRAFT_STATUS_COLORS[d.draft_status] || ''}`}>
                        {d.draft_status === 'pending_review' ? 'Pending Review' : d.draft_status === 'approved' ? 'Approved' : 'Needs Revision'}
                      </Badge>
                    </div>
                  ))}
                  {emailDrafts.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No email drafts yet.</p>}
                </div>
              </TabsContent>
            )}

            {/* To-Dos Tab */}
            {!isCreate && (
              <TabsContent value="todos" className="mt-3 space-y-3">
                <p className="text-xs text-muted-foreground">{todos.filter(t => t.status !== 'Completed').length} active to-do{todos.filter(t => t.status !== 'Completed').length !== 1 ? 's' : ''} (created via Next Steps)</p>
                <div className="space-y-1.5">
                  {todos.map(t => (
                    <div key={t.id} className={`flex items-start gap-2.5 p-2.5 rounded-md border ${t.status === 'Completed' ? 'border-border/50 bg-secondary/20 opacity-60' : 'border-border bg-background'}`} data-testid={`lead-todo-${t.id}`}>
                      <Checkbox
                        checked={t.status === 'Completed'}
                        onCheckedChange={() => toggleTodoStatus(t.id, t.status)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        {editingTodoId === t.id ? (
                          <div className="space-y-2">
                            <Input
                              value={editTodoForm.title}
                              onChange={e => setEditTodoForm({ ...editTodoForm, title: e.target.value })}
                              className="bg-card border-border text-sm"
                              placeholder="Title"
                              data-testid={`edit-todo-title-${t.id}`}
                            />
                            <RichTextEditor
                              value={editTodoForm.description}
                              onChange={v => setEditTodoForm({ ...editTodoForm, description: v })}
                              placeholder="Description..."
                            />
                            <div className="grid grid-cols-3 gap-2">
                              <Select value={editTodoForm.assigned_to || 'none'} onValueChange={v => setEditTodoForm({ ...editTodoForm, assigned_to: v === 'none' ? '' : v })}>
                                <SelectTrigger className="bg-card border-border text-xs h-8" data-testid={`edit-todo-assignee-${t.id}`}>
                                  <SelectValue placeholder="Assignee" />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  <SelectItem value="none" className="text-xs text-muted-foreground">Unassigned</SelectItem>
                                  {(users || []).filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Input
                                type="date"
                                value={editTodoForm.due_date}
                                onChange={e => setEditTodoForm({ ...editTodoForm, due_date: e.target.value })}
                                className="bg-card border-border text-xs h-8"
                              />
                              <Select value={editTodoForm.priority} onValueChange={v => setEditTodoForm({ ...editTodoForm, priority: v })}>
                                <SelectTrigger className="bg-card border-border text-xs h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  {['Low', 'Medium', 'High', 'Urgent'].map(p => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <Select value={editTodoForm.status} onValueChange={v => setEditTodoForm({ ...editTodoForm, status: v })}>
                              <SelectTrigger className="bg-card border-border text-xs h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {['Not Started', 'In Progress', 'Blocked', 'Completed'].map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={saveEditTodo} className="bg-primary text-primary-foreground text-xs h-7" data-testid={`save-edit-todo-${t.id}`}>Save</Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingTodoId(null)} className="text-xs h-7">Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className={`text-sm ${t.status === 'Completed' ? 'line-through text-muted-foreground' : ''}`}>{t.title}</p>
                            {t.description && <div className="mt-0.5"><RichTextDisplay value={t.description} collapsedHeight={20} /></div>}
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                              {t.assignee_name && <span className="flex items-center gap-0.5"><User className="h-2.5 w-2.5" />{t.assignee_name}</span>}
                              {t.due_date && <span className="flex items-center gap-0.5"><Calendar className="h-2.5 w-2.5" />{t.due_date}</span>}
                              <Badge variant="outline" className={`text-xs ${t.priority === 'High' ? 'text-destructive border-destructive/20' : t.priority === 'Medium' ? 'text-amber-400 border-amber-500/20' : ''}`}>{t.priority}</Badge>
                            </div>
                          </>
                        )}
                      </div>
                      {editingTodoId !== t.id && (
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-primary shrink-0" onClick={() => openEditTodo(t)} data-testid={`edit-lead-todo-${t.id}`}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {todos.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No to-dos yet. Add a Next Step to create a linked To-Do.</p>}
                </div>
              </TabsContent>
            )}

            {/* Time Logged Tab */}
            {!isCreate && (
              <TabsContent value="time-logged" className="mt-3 space-y-3" data-testid="lead-time-logged-tab">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{timeEntries.length} timesheet entries</p>
                  <Badge className="bg-primary/10 text-primary border-primary/20">
                    {timeEntries.reduce((s, t) => s + (Number(t.hours) || 0), 0).toFixed(2)} total hours
                  </Badge>
                </div>
                {timeEntries.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">No time logged against this lead yet</p>
                ) : (
                  <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                    {timeEntries.map(t => (
                      <Card key={t.id} className="border-border bg-background shadow-none" data-testid={`lead-time-entry-${t.id}`}>
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-mono text-muted-foreground">{t.date}</span>
                            <span className="text-sm font-bold text-primary">{t.hours}h</span>
                            <Badge className={`text-xs ${t.status === 'Approved' ? 'bg-emerald-500/10 text-emerald-400' : t.status === 'Submitted' ? 'bg-amber-500/10 text-amber-400' : t.status === 'Rejected' ? 'bg-destructive/10 text-destructive' : 'bg-secondary text-muted-foreground'}`}>{t.status}</Badge>
                            {!t.is_billable && <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{t.user_name}</p>
                          {t.description && <div className="mt-1"><RichTextDisplay value={t.description} collapsedHeight={20} /></div>}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

            {/* Status Log Tab */}
            {!isCreate && (
              <TabsContent value="status-log" className="mt-3" data-testid="lead-status-log-tab-content">
                <EntityStatusLogTab
                  entityType="lead"
                  entityId={leadId}
                  entityLabel={[lead?.contact_name, lead?.project_address].filter(Boolean).join(' - ')}
                  statusLog={statusLog}
                  onChange={() => leadId && loadLeadData(leadId)}
                />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>

    <Dialog open={closeWonOpen} onOpenChange={setCloseWonOpen}>
      <DialogContent className="max-w-md" data-testid="close-won-client-dialog">
        <DialogHeader>
          <DialogTitle>Link a Client to the Proposal</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2 mb-1">
          This Lead has no linked Client yet. Pick an existing one or create a new client record from the Lead&apos;s company info.
        </p>
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setCloseWonClientMode('new')}
            className={`flex-1 text-xs py-2 px-3 rounded border ${closeWonClientMode === 'new' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground'}`}
            data-testid="close-won-mode-new"
          >Create new client from this Lead</button>
          <button
            onClick={() => setCloseWonClientMode('existing')}
            className={`flex-1 text-xs py-2 px-3 rounded border ${closeWonClientMode === 'existing' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground'}`}
            data-testid="close-won-mode-existing"
          >Pick existing client</button>
        </div>
        {closeWonClientMode === 'new' ? (
          <div className="text-xs text-muted-foreground bg-card border border-border rounded p-3 space-y-1" data-testid="close-won-new-preview">
            <p><span className="text-foreground font-medium">Name:</span> {(form.company || lead?.company || form.contact_name || lead?.contact_name) || <span className="italic">(empty)</span>}</p>
            <p><span className="text-foreground font-medium">Email:</span> {(form.email || lead?.email) || <span className="italic">(empty)</span>}</p>
            <p><span className="text-foreground font-medium">Phone:</span> {(form.phone || lead?.phone) || <span className="italic">(empty)</span>}</p>
            <p><span className="text-foreground font-medium">Address:</span> {(form.project_address || lead?.project_address) || <span className="italic">(empty)</span>}</p>
          </div>
        ) : (
          <div data-testid="close-won-existing-pick">
            <SearchableSelect
              value={closeWonExistingClientId}
              onValueChange={setCloseWonExistingClientId}
              placeholder="Search clients..."
              options={(allClients || []).map(c => ({ value: c.id, label: c.name }))}
              searchPlaceholder="Type to filter..."
            />
          </div>
        )}
        <DialogFooter className="mt-3">
          <Button variant="ghost" size="sm" onClick={() => setCloseWonOpen(false)} data-testid="close-won-cancel">Cancel</Button>
          <Button
            size="sm"
            disabled={closeWonClientMode === 'existing' && !closeWonExistingClientId}
            onClick={() => {
              const choice = closeWonClientMode === 'new'
                ? { mode: 'new' }
                : { mode: 'existing', client_id: closeWonExistingClientId };
              setCloseWonOpen(false);
              doCreateProposal(choice);
            }}
            data-testid="close-won-confirm"
          >Create Proposal</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
